import React, { useCallback, useEffect, useState } from 'react';
import { commercesApi, COMMERCE_CATEGORIES, CATEGORY_LABEL } from '../../services/commercesApi';
import { getToken } from '../auth';
import '../styles/Annuaire.css';
import './admin-form.css';

const EMPTY_FORM = {
  name: '', description: '', category: 'service',
  address: '', phone: '', email: '', hours: '',
  rating: 0, reviews: 0,
  featured: false, published: true,
  imageSeed: '', imageUrl: '',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Annuaire = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await commercesApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        featured: filterFeatured === 'yes' ? true : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory, filterFeatured, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { commerce } = await commercesApi.adminGet(token, id);
      setForm({
        name: commerce.name,
        description: commerce.description || '',
        category: commerce.category,
        address: commerce.address || '',
        phone: commerce.phone || '',
        email: commerce.email || '',
        hours: commerce.hours || '',
        rating: commerce.rating || 0,
        reviews: commerce.reviews || 0,
        featured: commerce.featured,
        published: commerce.published,
        imageSeed: commerce.imageSeed || '',
        imageUrl: commerce.imageUrl || '',
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger le commerce : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        address: form.address,
        phone: form.phone,
        email: form.email,
        hours: form.hours,
        rating: Number(form.rating),
        reviews: parseInt(form.reviews, 10) || 0,
        featured: form.featured,
        published: form.published,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
      };
      if (editingId) await commercesApi.adminUpdate(token, editingId, payload);
      else           await commercesApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement « ${name} » ?`)) return;
    try {
      await commercesApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleFeatured  = async (id, v) => { try { await commercesApi.adminUpdate(token, id, { featured:  !v }); load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (id, v) => { try { await commercesApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const catPillClass = (cat) => {
    if (cat === 'restaurant') return 'sunset';
    if (cat === 'boutique') return 'gold';
    if (cat === 'hebergement' || cat === 'service') return 'ocean';
    return '';
  };

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Annuaire</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un commerce, une adresse…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Référencer
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Annuaire · {total} commerce{total > 1 ? 's' : ''}
            </span>
            <h1>Les commerces,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Référencer, éditer, mettre en avant, publier ou retirer. Synchronisé en live avec /annuaire.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {COMMERCE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
            <select
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              <option value="all">Tous</option>
              <option value="yes">Coups de cœur</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 48 }} />
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Adresse</th>
                <th>Note</th>
                <th>★</th>
                <th>Visible</th>
                <th>MAJ</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun commerce. Cliquez « Référencer » pour ajouter le premier.
                </td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} onClick={() => openEdit(c.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div
                      className="av sq"
                      style={{
                        width: 36, height: 36, borderRadius: 9, backgroundSize: 'cover', backgroundPosition: 'center',
                        backgroundImage: c.imageUrl
                          ? `url('${c.imageUrl}')`
                          : `url('https://picsum.photos/seed/${c.imageSeed || `cb-ann-${c.id}`}/120/120')`,
                      }}
                    />
                  </td>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>{c.name}</b>
                    {c.description && (
                      <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.description}
                      </small>
                    )}
                  </td>
                  <td>
                    <span className={`cat-pill ${catPillClass(c.category)}`}>
                      {CATEGORY_LABEL[c.category] || c.category}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--char-500)', letterSpacing: '.04em' }}>
                    {c.address || '—'}
                    {c.hours && <small style={{ display: 'block', marginTop: 2, fontSize: 10 }}>{c.hours}</small>}
                  </td>
                  <td>
                    {c.rating > 0 ? (
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                        <b style={{ fontFamily: 'var(--sans)', fontSize: 14 }}>★ {c.rating.toFixed(1)}</b>
                        <small style={{ marginLeft: 6, color: 'var(--char-500)' }}>({c.reviews})</small>
                      </span>
                    ) : <span style={{ color: 'var(--char-500)' }}>—</span>}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      title={c.featured ? 'Retirer des coups de cœur' : 'Ajouter aux coups de cœur'}
                      onClick={() => onToggleFeatured(c.id, c.featured)}
                      style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: c.featured ? 'var(--sunset)' : 'var(--bone-2)',
                        color: c.featured ? 'var(--bone)' : 'var(--char-500)',
                        border: c.featured ? '1px solid var(--sunset)' : '1px solid var(--line)',
                        cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      ★
                    </button>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <span
                      className={`status-pill ${c.published ? 'live' : 'muted'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onTogglePublished(c.id, c.published)}
                      title={c.published ? 'Cliquer pour dépublier' : 'Cliquer pour publier'}
                    >
                      {c.published ? 'Publié' : 'Caché'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                    {fmtDate(c.updatedAt)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button title="Éditer" onClick={() => openEdit(c.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(c.id, c.name)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} commerce{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Éditer le commerce' : 'Nouveau commerce'}</span>
                <h2 className="admin-modal-title">{editingId ? form.name || '—' : 'Référencer une adresse'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form onSubmit={onSubmit} className="admin-form">
              <div className="admin-form-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
                <Field label="Nom du commerce">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Ex : Chez Mami Coumba"
                  />
                </Field>
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {COMMERCE_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </Field>
              </div>

              <Field label="Description courte">
                <textarea
                  rows={2}
                  maxLength={240}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Ce qu'on y trouve, pourquoi y aller — une à deux phrases"
                />
              </Field>

              <div className="admin-form-row">
                <Field label="Adresse">
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Rue 8 · Centre"
                  />
                </Field>
                <Field label="Horaires">
                  <input
                    type="text"
                    value={form.hours}
                    onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                    placeholder="11h–22h  ·  ou  Fermé lundi"
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Téléphone">
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+221 77 ..."
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="contact@..."
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label={`Note · ${Number(form.rating).toFixed(1)}/5`}>
                  <input
                    type="range"
                    min={0} max={5} step={0.1}
                    value={form.rating}
                    onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
                    style={{ accentColor: 'var(--sunset)' }}
                  />
                </Field>
                <Field label="Nombre d'avis">
                  <input
                    type="number"
                    min={0}
                    value={form.reviews}
                    onChange={(e) => setForm((f) => ({ ...f, reviews: e.target.value }))}
                  />
                </Field>
              </div>

              <Field label="Image · seed Picsum ou URL">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-ann-mami-coumba  ·  ou  https://…"
                />
              </Field>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  />
                  <span>★ Coup de cœur (mis en avant)</span>
                </label>
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur /annuaire' : 'Caché (brouillon)'}</span>
                </label>
              </div>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Référencer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Annuaire;
